import { useCallback, useEffect, useState } from 'react'
import { useAuthToken } from './useAuthToken'
import { CLOUD_API, CLOUD_NOT_CONNECTED } from './shared'
import { money } from '@core/utils/format'
import { httpErrorText } from '@core/utils/httpError'
import { hint, table, tableWrap, td, tdNum, th, thNum } from '@ui/classes'
import { errBanner } from '@ui/admin'

interface CloudWriteoff {
  writeoff_id: string
  receipt_id: string | null
  reason_code: string
  origin?: 'manual' | 'return_writeoff' | 'marking_hold' | null
  comment: string | null
  items: Array<{ name?: string; qty?: number; price?: number }>
  total: string
  user_name: string | null
  zone_id: string | null
  created_at: string
}

const REASON_LABELS: Record<string, string> = {
  damage: 'Порча',
  expired: 'Истёк срок',
  breakage: 'Бой',
  supplier_return: 'Возврат поставщику',
  other: 'Другое'
}

/** 65.14/9d: источник документа списания. */
const ORIGIN_LABELS: Record<string, string> = {
  manual: 'Вручную',
  return_writeoff: 'Возврат + списание',
  marking_hold: 'Из чеков на проверке'
}

/** Раздел «Списания» (ARCH-TASK-65.10). */
export function WriteoffsSection({ refreshKey = 0 }: { refreshKey?: number }) {
  const token = useAuthToken()
  const [rows, setRows] = useState<CloudWriteoff[]>([])
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!token) {
      setRows([])
      return
    }
    try {
      const resp = await fetch(`${CLOUD_API}/sync/writeoffs?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!resp.ok) {
        setErr(httpErrorText('Загрузка списаний', resp.status))
        return
      }
      const data = (await resp.json()) as { writeoffs: CloudWriteoff[] }
      setRows(data.writeoffs ?? [])
      setErr(null)
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [token])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  return (
    <div>
      {!token && <p className={hint}>{CLOUD_NOT_CONNECTED}</p>}
      {err && <p className={errBanner}>{err}</p>}
      {token && rows.length === 0 && <p className={hint}>Списаний пока нет.</p>}
      {rows.length > 0 && (
        <div className={tableWrap}>
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>Дата</th>
                <th className={th}>Причина</th>
                <th className={th}>Состав</th>
                <th className={th}>Чек</th>
                <th className={th}>Кто</th>
                <th className={thNum}>Сумма</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.writeoff_id}>
                  <td className={td}>{new Date(w.created_at).toLocaleString('ru-RU')}</td>
                  <td className={td}>
                    {REASON_LABELS[w.reason_code] ?? w.reason_code}
                    {w.origin && w.origin !== 'manual' && (
                      <span
                        className={
                          w.origin === 'marking_hold'
                            ? 'ml-1 rounded bg-amber-100 px-1.5 py-0.5 font-sans text-tiny font-semibold text-amber-800'
                            : 'ml-1 rounded bg-soft px-1.5 py-0.5 font-sans text-tiny font-semibold text-muted'
                        }
                      >
                        {ORIGIN_LABELS[w.origin] ?? w.origin}
                      </span>
                    )}
                    {w.comment && (
                      <span className="block text-tiny text-muted">{w.comment}</span>
                    )}
                  </td>
                  <td className={td}>
                    {w.items
                      .map((i) => `${i.name ?? '—'}×${i.qty ?? 1}`)
                      .join(', ')}
                  </td>
                  <td className={td}>{w.receipt_id ? `${w.receipt_id.slice(0, 8)}…` : '—'}</td>
                  <td className={td}>{w.user_name ?? '—'}</td>
                  <td className={tdNum}>{money(Number(w.total))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
